/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import {
    AfterContentChecked,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Inject,
    Injectable,
    Input,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { Actions, Select, Store } from '@ngxs/store';
import { MapConfigState } from '../../map/state/map-config.state';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { LoggerService } from '../../shared/services/logger.service';
import { MapConfigService } from '../../map/services/map-config.service';
import { RedlineConfigService } from '../../../components/redline/services/redline-config.service';
import { ReportService } from '../services/report.service';
import { QueryService } from '../../map/services/query.service';
import { DeviceService } from '../../shared/services/device.service';
import { MatDialog } from '@angular/material/dialog';
import { ReportDisplayOverlayRef } from './report-display.overlay-ref';
import { ClearReportOptions } from '../state/report.actions';
import { map } from 'rxjs/operators';
import { ClearSnapshots, RestoreSnapshot } from '../../../ngxs/snapshotPlugin/ngxs-snapshot.actions';
import { NgxsSnapshotState } from '../../../ngxs/snapshotPlugin/ngxs-snapshot.state';
import { ResizeEvent } from '../../../core/ui/dash-gis-ng2-resizable/interfaces';
import { DashGisUtil } from '../../shared/dash-gis-util';

@Component({ template: '' })
@Injectable()
export class ReportDisplayBaseComponent implements OnInit, AfterContentChecked, OnDestroy {
    @Input() reportKeys;

    @Output() edit = new EventEmitter<any>();
    @Output() closePanel = new EventEmitter<void>();

    panelWidth$ = new BehaviorSubject<string>('');
    isHistoryEmpty$: Observable<boolean>;

    uid: string;

    recordCount$ = new BehaviorSubject<number>(0);
    reportDisplayName$ = new BehaviorSubject<string>('Report');
    reportTitle$: Observable<string>;

    protected isMinimized: boolean;

    protected disposeSubject = new Subject<void>();
    protected dispose$: Observable<void>;

    protected historyGroup: string;

    @ViewChild('videoDialog')
    protected videoTemplate: TemplateRef<any>;

    constructor(
        protected logger: LoggerService,
        protected mapConfig: MapConfigService,
        protected redlineConfig: RedlineConfigService,
        protected reportService: ReportService,
        protected queryService: QueryService,
        protected deviceService: DeviceService,
        protected dialog: MatDialog,
        protected actions$: Actions,
        protected store: Store,
        protected cdr: ChangeDetectorRef,
        @Inject(ReportDisplayOverlayRef) protected overlayRef: ReportDisplayOverlayRef
    ) {
        this.uid = DashGisUtil.uid();
        this.dispose$ = this.disposeSubject.asObservable();

        this.reportTitle$ = this.initReportTitle();
        this.isHistoryEmpty$ = this.store.select(NgxsSnapshotState.snapshotCount).pipe(
            map((fn) => fn(this.historyGroup)),
            map((count) => count === 0)
        );
    }

    ngOnInit() {}

    ngAfterContentChecked() {}

    ngOnDestroy() {
        this.store.dispatch(new ClearSnapshots({ group: this.historyGroup }));
        this.store.dispatch(new ClearReportOptions({ id: this.uid }));
        this.disposeSubject.next();
    }

    onResizeEnd(event: ResizeEvent) {
        this.panelWidth$.next(`${event.rectangle.width}px`);
        this.overlayRef.updateSize({
            width: event.rectangle.width,
            height: event.rectangle.height,
        });
    }

    setRecordCount(count: number) {
        this.recordCount$.next(count);
    }

    setReportTitle(newTitle: string) {
        let title = this.reportDisplayName$.getValue();
        if (newTitle && title !== newTitle) {
            title = newTitle;
            this.reportDisplayName$.next(title);
        }
    }

    minimize() {
        this.isMinimized = true;
    }

    restore() {
        this.isMinimized = false;
    }

    historyBack() {
        this.store.dispatch(new RestoreSnapshot({ group: this.historyGroup }));
    }

    private initReportTitle(): Observable<string> {
        return combineLatest([this.reportDisplayName$, this.recordCount$]).pipe(
            map(([title, count]: [string, number]): string => {
                if (count > 1) {
                    title = `${title} (${count} records)`;
                }
                return title;
            })
        );
    }
}
